import { useMatchMedia } from '@udemy/hooks';
import { useContentGrid, withinOrWiderThan } from '@udemy/react-layout-components';
import { safelySetInnerHTML } from '@udemy/shared-utils';
import { tokens } from '@udemy/styles';
import classNames from 'classnames';
import React from 'react';
import { CourseCardDetails } from './course-card-details.react-component';
import { CourseCardImage, CourseCardImageContext } from './course-card-image.react-component';
import { CourseCardInstructors } from './course-card-instructors.react-component';
import { CourseCardPrice, CourseCardPriceContext } from './course-card-price.react-component';
import { CourseCardRatings } from './course-card-ratings.react-component';
import { CourseCardRibbons } from './course-card-ribbons.react-component';
import { CourseCardTitle, CourseCardTitleContext } from './course-card-title.react-component';
import styles from "./course-card.module.css";
/**
 * Style props for `StaticPriceText` and `DynamicPriceText` components to make
 * it more convenient to use those components as price text.
 *
 * @see Price text components
 * {@link @udemy/react-merchandising-components#StaticPriceText} and
 * {@link @udemy/react-merchandising-components#DynamicPriceText}
 *
 * @privateRemarks
 *
 * These classes are exported by this card component rather than the price sub-component
 * because they are used to manage layout at the card level.
 */

export const priceTextStyleProps = {
  className: styles['price-text-base-price-text-component'],
  listPriceClassName: styles['price-text-base-price-text-component-list-price'],
  discountPriceClassName: styles['price-text-base-price-text-component-discount-price']
};
/**
 * Variants for card sizes and layouts
 *
 * @remarks
 *
 * - `small` - square image; can be tiled; has flexible or fixed width constraints
 * - `medium` - rectangular image; suitable for rows of multiple cards (e.g., in carousel)
 * - `large` - meant to consume full width of the content area
 * - `wide-n-columns` - meant for horizontal layout sections with a single card the One Pane
 * Page Layout grid (viewport-based grid); number of columns in size must match the number of
 * columns consumed by the actual card (e.g., 4, 7, 9, 10, or 12)
 * - `wide-content-grid` - meant for horizontal layout sections with a single card in the Content Area Grid
 */

/**
 * Convert column count to `CardSize` prop for viewport-based, wide layouts
 *
 * @param columnCount
 * @returns viewport-based wide card size
 */
export const courseCardSizeFromColumns = columnCount => {
  switch (columnCount) {
    case 4:
      return 'wide-4-columns';

    case 7:
      return 'wide-7-columns';

    case 9:
      return 'wide-9-columns';

    case 10:
      return 'wide-10-columns';

    default:
      return 'wide-12-columns';
  }
};
/**
 * Props for `CourseCard` component.
 *
 * @remarks
 *
 * Extends props for `<div>`, which are applied to the containing `<div>` element.
 *
 * @see `CourseCard` for description of sub-component slots specified in these props.
 */

/**
 * Return className for any given course card size prop.
 *
 * @internal
 */
const sizeClassName = (size, contentGridColumnCount) => {
  const contentGridColumnsClassName = contentGridColumnCount === null ? 'wide-content-grid-4-columns' : `wide-content-grid-${contentGridColumnCount}-columns`;

  switch (size) {
    case 'wide-12-columns':
    case 'wide-10-columns':
    case 'wide-9-columns':
    case 'wide-7-columns':
    case 'wide-4-columns':
      return classNames(styles['wide-grid'], styles[size]);

    case 'wide-content-grid':
      return classNames(styles['wide-content-grid'], styles[contentGridColumnsClassName]);
  }

  return styles[size];
};
/**
 * Return `size` prop for `CourseImage` from card `size` prop value.
 *
 * @internal
 */


const cardSizeToImageSize = size => {
  switch (size) {
    case 'wide-content-grid':
    case 'wide-12-columns':
    case 'wide-10-columns':
    case 'wide-9-columns':
    case 'wide-7-columns':
    case 'wide-4-columns':
      return 'large';
  }

  return size;
};
/**
 * ### CourseCard
 *
 * Card component for a course.
 *
 * @remarks
 *
 * Forwards a `ref` to the containing `<div>` element.
 *
 * #### Slots
 *
 * The card is divided into distinct rendering areas ("slots"):
 * - ribbons
 * - details
 * - image
 * - instructors
 * - price
 * - ratings
 * - title
 *
 * Each slot has two props. One is for the props passed to a default
 * sub-component (e.g., `imageProps` for the image slot).
 * The other slot prop will completely override the slot nodes (e.g.,
 * `image`). If both props for a slot are specified, `CourseCard`
 * will render the override prop (e.g., `image`) instead of the
 * default component.
 *
 * Each slot is rendered independently, and is omitted by default.
 * This pattern allows for a mixing of default and custom rendering.
 */


export const CourseCard = Object.assign( /*#__PURE__*/React.forwardRef((_ref, ref) => {
  let {
    ribbons,
    ribbonsProps,
    badges,
    badgesProps,
    children,
    details,
    detailsProps,
    headline,
    image,
    imageProps,
    instructors,
    instructorsProps,
    price,
    priceProps,
    ratings,
    ratingsProps,
    title,
    titleProps,
    showDetails = true,
    size = 'medium',
    width = 'flexible',
    ...htmlProps
  } = _ref;
  const layout12ColumnPage4ColumnsMinToken = tokens['layout-12-column-page-12-columns-min'];
  const isSmallMinViewport = useMatchMedia('sm-min');
  const isLargeMinViewport = useMatchMedia('lg-min');
  const is12ColumnMinViewport = useMatchMedia(`(min-width: ${layout12ColumnPage4ColumnsMinToken})`);
  const {
    columnCount: contentGridColumnCount,
    range: contentAreaRange
  } = useContentGrid();
  const isWideLayout = size === 'large' && isSmallMinViewport || ['wide-12-columns'].includes(size) && is12ColumnMinViewport || ['wide-9-columns', 'wide-10-columns'].includes(size) && isLargeMinViewport || ['wide-content-grid'].includes(size) && contentGridColumnCount === 12 || ['wide-content-grid'].includes(size) && [9, 10].includes(contentGridColumnCount !== null && contentGridColumnCount !== void 0 ? contentGridColumnCount : 4) && withinOrWiderThan(contentAreaRange !== null && contentAreaRange !== void 0 ? contentAreaRange : 'xxxs', 'lg');
  const priceLayoutDirection = isWideLayout ? 'vertical' : 'horizontal';
  const showHeadline = showDetails && headline && (size === 'large' || size.startsWith('wide-'));
  const renderedPrice = price || priceProps && /*#__PURE__*/React.createElement(CourseCardPrice, priceProps);
  const hasPrice = !!renderedPrice;
  return /*#__PURE__*/React.createElement("div", Object.assign({}, htmlProps, {
    "data-purpose": "container",
    className: classNames(htmlProps.className, styles.container, sizeClassName(size, contentGridColumnCount), {
      [styles.fixed]: width === 'fixed'
    }),
    ref: ref
  }), /*#__PURE__*/React.createElement("div", {
    className: styles['image-container']
  }, /*#__PURE__*/React.createElement(CourseCardImageContext.Provider, {
    value: {
      size: cardSizeToImageSize(size),
      className: styles['course-image']
    }
  }, image || imageProps && /*#__PURE__*/React.createElement(CourseCardImage, imageProps))), /*#__PURE__*/React.createElement("div", {
    className: classNames(styles['main-content'], {
      [styles['has-price-text']]: hasPrice
    })
  }, /*#__PURE__*/React.createElement("div", {
    className: styles['title-container']
  }, /*#__PURE__*/React.createElement(CourseCardTitleContext.Provider, {
    value: {
      size: size === 'small' ? 'small' : 'medium'
    }
  }, title || titleProps && /*#__PURE__*/React.createElement(CourseCardTitle, titleProps))), showHeadline && /*#__PURE__*/React.createElement("p", Object.assign({
    className: classNames('ud-text-sm', styles['course-headline'])
  }, safelySetInnerHTML({
    descriptionOfCaller: 'course-card:course-headline',
    html: headline
  }))), instructors || instructorsProps && /*#__PURE__*/React.createElement(CourseCardInstructors, instructorsProps), ratings || ratingsProps && /*#__PURE__*/React.createElement(CourseCardRatings, ratingsProps), showDetails && (details || detailsProps && /*#__PURE__*/React.createElement(CourseCardDetails, detailsProps)), /*#__PURE__*/React.createElement("div", {
    className: styles['price-text-container']
  }, /*#__PURE__*/React.createElement(CourseCardPriceContext.Provider, {
    value: {
      layoutDirection: priceLayoutDirection
    }
  }, renderedPrice)), /*#__PURE__*/React.createElement("div", {
    className: styles['ribbons-container']
  }, badges || ribbons || badgesProps && /*#__PURE__*/React.createElement(CourseCardRibbons, badgesProps) || ribbonsProps && /*#__PURE__*/React.createElement(CourseCardRibbons, ribbonsProps)), children));
}), {
  /* eslint-disable @typescript-eslint/naming-convention */
  Ribbons: CourseCardRibbons,

  /** @deprecated use `CourseCard.Ribbons` instead. */
  Badges: CourseCardRibbons,
  Details: CourseCardDetails,
  Image: CourseCardImage,
  Instructors: CourseCardInstructors,
  Price: CourseCardPrice,
  Ratings: CourseCardRatings,
  Title: CourseCardTitle
  /* eslint-enable @typescript-eslint/naming-convention */

});
//# sourceMappingURL=course-card.react-component.js.map